import React, { useState } from 'react';
import { ChevronDown, MessageCircle } from 'lucide-react';
import { FAQSchema } from './StructuredData';

interface FAQ {
  question: string;
  answer: string;
}

const FAQSection: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const faqs: FAQ[] = [
    {
      question: "Was ist der EU AI Act?",
      answer: "Der EU AI Act ist die weltweit erste umfassende Regulierung für Künstliche Intelligenz und gilt ab 2025 in der gesamten EU. Er klassifiziert KI-Systeme nach Risikoklassen und legt entsprechende Compliance-Anforderungen fest. Unternehmen müssen nachweisen, dass ihre Mitarbeiter über die erforderliche KI-Kompetenz verfügen."
    },
    {
      question: "Wen betrifft der EU AI Act?",
      answer: "Der EU AI Act betrifft alle Unternehmen in der EU, die KI-Systeme entwickeln, einsetzen oder vertreiben. Dies schließt auch kleine und mittelständische Unternehmen ein, die Tools wie ChatGPT, Microsoft Copilot oder andere KI-Anwendungen nutzen. Der Nachweis von KI-Kompetenz ist für viele Positionen verpflichtend."
    },
    {
      question: "Welche Strafen drohen bei Nichteinhaltung?",
      answer: "Bei Verstößen gegen den EU AI Act drohen Bußgelder von bis zu 35 Millionen Euro oder 7 Prozent des weltweiten Jahresumsatzes, je nachdem, welcher Betrag höher ist. Zudem können Haftungsrisiken und Reputationsschäden entstehen. Die frühzeitige Schulung Ihrer Mitarbeiter minimiert diese Risiken erheblich."
    },
    {
      question: "Ist ein Zertifikat wirklich notwendig?",
      answer: "Ja, der EU AI Act verlangt den Nachweis von KI-Kompetenz für Mitarbeiter, die mit KI-Systemen arbeiten. Ein anerkanntes Zertifikat dokumentiert diese Qualifikation gegenüber Behörden, Kunden und Geschäftspartnern. Es schützt Ihr Unternehmen rechtlich und stärkt das Vertrauen in Ihre KI-Nutzung."
    },
    {
      question: "Wie lange dauert die Schulung?",
      answer: "Unser Online-Kurs ist so konzipiert, dass Sie ihn in etwa 45 Minuten durcharbeiten können. Sie lernen in Ihrem eigenen Tempo und können jederzeit pausieren. Nach erfolgreichem Abschlusstest erhalten Sie sofort Ihr personalisiertes Zertifikat zum Download."
    },
    {
      question: "Ist das Zertifikat rechtlich anerkannt?",
      answer: "Unser Zertifikat erfüllt die Anforderungen des EU AI Act zur Dokumentation von KI-Kompetenz. Es basiert auf den offiziellen EU-Richtlinien und kann bei Audits, Zertifizierungen und gegenüber Behörden vorgelegt werden. Jedes Zertifikat ist digital verifizierbar und fälschungssicher."
    },
    {
      question: "Kann ich mehrere Mitarbeiter schulen?",
      answer: "Ja, wir bieten spezielle Enterprise-Lösungen für Unternehmen an. Sie können mehrere Lizenzen erwerben oder einen individuellen Zugang für Ihr Team einrichten. Kontaktieren Sie uns für maßgeschneiderte Angebote und Mengenrabatte für größere Teams."
    }
  ];

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const scrollToContact = () => {
    const contactSection = document.getElementById('contact');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <>
      <FAQSchema faqs={faqs} />
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
                Häufig gestellte Fragen zum EU AI Act
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Alles, was Sie über den EU AI Act und unsere Schulung wissen müssen
              </p>
            </div>

            <div className="space-y-3">
              {faqs.map((faq, index) => (
                <div
                  key={index}
                  className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden transition-all duration-300 hover:shadow-lg"
                >
                  <button
                    onClick={() => toggleFAQ(index)}
                    className="w-full px-6 py-5 text-left flex items-center justify-between bg-white hover:bg-gray-50 transition-colors group"
                    aria-expanded={openIndex === index}
                  >
                    <span className="text-lg font-semibold text-gray-800 pr-4 group-hover:text-[#2574B5] transition-colors">
                      {faq.question}
                    </span>
                    <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300 ${
                      openIndex === index
                        ? 'bg-[#2574B5] text-white rotate-180'
                        : 'bg-gray-100 text-gray-600 group-hover:bg-[#2574B5] group-hover:text-white'
                    }`}>
                      <ChevronDown className="h-5 w-5" />
                    </div>
                  </button>
                  <div
                    className={`transition-all duration-300 ease-in-out ${
                      openIndex === index
                        ? 'max-h-96 opacity-100'
                        : 'max-h-0 opacity-0'
                    } overflow-hidden`}
                  >
                    <div className="px-6 py-5 bg-gradient-to-r from-blue-50 to-gray-50 border-t border-gray-200">
                      <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-12 text-center bg-white rounded-lg shadow-md border border-gray-200 p-8">
              <MessageCircle className="h-12 w-12 text-[#2574B5] mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-800 mb-2">
                Noch weitere Fragen?
              </h3>
              <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
                Unser Team steht Ihnen gerne zur Verfügung. Kontaktieren Sie uns für eine persönliche Beratung zu Ihren spezifischen Anforderungen.
              </p>
              <button
                onClick={scrollToContact}
                className="inline-flex items-center bg-[#EE7F01] hover:bg-orange-600 text-white font-medium py-3 px-8 rounded-md transition-colors shadow-sm hover:shadow-md"
              >
                Jetzt Kontakt aufnehmen
                <svg
                  className="ml-2 h-5 w-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17 8l4 4m0 0l-4 4m4-4H3"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default FAQSection;
